import { useAppSelector } from "@/app/hooks";
import { LeaseComp } from "@/features/leaseComps/types/leaseComps.types";
import type { Property } from "@/features/survey/types";
import { getIconData, getLeaseCompIconData } from "@/shared/utils/map";

interface MarketMapLegendsProps {
  mode?: string;
  pageProperties?: Property[];
  pageLeaseComps?: LeaseComp[];
  startIndexFrom?: number;
  startLeaseIndexFrom?: number;
}

const MarketMapLegends = ({
  mode,
  pageProperties,
  pageLeaseComps,
  startIndexFrom = 0,
  startLeaseIndexFrom = 0,
}: MarketMapLegendsProps) => {
  const survey = useAppSelector(
    (state) => state.interactiveSurvey.currentSurvey,
  );

  // Get legend display fields from survey - using type assertion for properties that may not be in Survey type
  const surveyWithCustomFields = survey as any;
  const legendFields = surveyWithCustomFields?.customLegends?.displayFields || {
    hasIcon: true,
    hasPropertyName: true,
    hasAddressLine1: true,
    hasAddressLine2: false,
    hasCity: true,
    hasState: true,
    hasZipCode: true,
    hasGroupName: false,
    hasGroupIconOnlyAvailable: false,
  };

  const properties = survey?.properties || [];
  const visibleProperties = pageProperties
    ? pageProperties
    : properties.filter((property: Property) => {
        if (property.isHidden) return false;
        if (
          surveyWithCustomFields?.isSurveyShortlistEnabled &&
          !property.isClientShortlist
        )
          return false;
        return true;
      });

  let propIndex = startIndexFrom;
  const propertyIndexMap: Record<string, number | string> =
    visibleProperties.reduce(
      (acc, property) => {
        if (property.isCurrentLocation) {
          return { ...acc, [property.propertyId || property.id]: "★" };
        }
        const currentIndex = propIndex + 1;
        propIndex = currentIndex;
        return { ...acc, [property.propertyId || property.id]: currentIndex };
      },
      {} as Record<string, number | string>,
    );

  // Format address line with proper commas
  const formatAddress = (property: Property): string => {
    const parts: string[] = [];
    if (legendFields.hasAddressLine1 && property.address1) {
      parts.push(property.address1);
    }
    if (legendFields.hasCity && property.city) {
      parts.push(property.city);
    }
    const stateZip: string[] = [];
    if (legendFields.hasState && (property as any).stateProvince) {
      stateZip.push((property as any).stateProvince);
    }
    if (legendFields.hasZipCode && property.postalCode) {
      stateZip.push(property.postalCode);
    }
    if (stateZip.length > 0) {
      parts.push(stateZip.join(" "));
    }
    return parts.join(", ");
  };

  // Render individual property legend item
  const renderPropertyLegendItem = (property: Property) => {
    const propertyKey = property.propertyId || property.id;
    const displayContent = propertyIndexMap[propertyKey];

    return (
      <div
        key={propertyKey}
        className="grid gap-3 items-start grid-cols-[auto_1fr]"
      >
        <div
          className="flex items-center justify-center w-5 h-5 rounded-full text-xs text-white"
          style={{ backgroundColor: getIconData(property).iconColor }}
        >
          {displayContent}
        </div>

        {/* Property Information */}
        <div className="overflow-hidden flex flex-col">
          {legendFields.hasPropertyName && (
            <div
              className="text-sm truncate leading-snug"
              title={
                property.name ||
                property.propertyName ||
                property.address1 ||
                ""
              }
            >
              {property.name ||
                property.propertyName ||
                property.address1 ||
                ""}
            </div>
          )}

          {formatAddress(property) && (
            <div
              className="text-sm truncate leading-snug"
              title={formatAddress(property)}
            >
              {formatAddress(property)}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Visible (non-hidden) lease comps — sliced and passed in by MarketMapWrapper
  const visibleLeaseComps = pageLeaseComps;

  // Render a single lease comp legend item (square marker + name/address)
  const renderLeaseCompLegendItem = (leaseComp: LeaseComp, idx: number) => {
    const fields = leaseComp.leaseCompFields;
    const { iconColor } = getLeaseCompIconData(leaseComp);

    const addressParts: string[] = [];
    if (legendFields.hasAddressLine1 && fields?.propertyAddress)
      addressParts.push(fields.propertyAddress);
    if (legendFields.hasCity && fields?.propertyCity)
      addressParts.push(fields.propertyCity);
    const stateZip: string[] = [];
    if (legendFields.hasState && fields?.propertyState)
      stateZip.push(fields.propertyState);
    if (legendFields.hasZipCode && fields?.propertyPostalCode)
      stateZip.push(fields.propertyPostalCode);
    if (stateZip.length > 0) addressParts.push(stateZip.join(" "));
    const address = addressParts.join(", ");

    return (
      <div
        key={leaseComp.leaseCompId}
        className="grid gap-3 items-start grid-cols-[auto_1fr]"
      >
        {/* Square marker */}
        <div
          className="flex items-center justify-center shrink-0 w-5 h-5 border border-white p-0.5 text-xs font-normal leading-4 tracking-[0.005em] text-white"
          style={{ backgroundColor: iconColor }}
        >
          {startLeaseIndexFrom + idx + 1}
        </div>

        {/* Lease Comp Information */}
        <div className="overflow-hidden flex flex-col">
          {legendFields.hasPropertyName && (
            <div
              className="text-sm truncate leading-snug"
              title={leaseComp.name || ""}
            >
              {leaseComp.name || ""}
            </div>
          )}
          {address && (
            <div className="text-sm truncate leading-snug" title={address}>
              {address}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Combine all items into a single ordered list for a unified 2-column layout
  const allItems: Array<
    | { kind: "property"; item: Property }
    | { kind: "lease"; item: LeaseComp; leaseIndex: number }
  > = [
    ...visibleProperties.map((p) => ({ kind: "property" as const, item: p })),
    ...(visibleLeaseComps?.map((lc, i) => ({
      kind: "lease" as const,
      item: lc,
      leaseIndex: i,
    })) || []),
  ];

  // Split items for portrait layout (two columns)
  const isPortrait =
    mode === "FlyerPreview" || survey?.pdfPaperSize === "Portrait";
  const midPoint = Math.ceil(allItems.length / 2);
  const leftItems = allItems.slice(0, midPoint);
  const rightItems = allItems.slice(midPoint);

  const renderItem = (
    entry:
      | { kind: "property"; item: Property }
      | { kind: "lease"; item: LeaseComp; leaseIndex: number },
  ) =>
    entry.kind === "property"
      ? renderPropertyLegendItem(entry.item)
      : renderLeaseCompLegendItem(entry.item, entry.leaseIndex);

  if (!survey || allItems.length === 0) {
    return null;
  }

  return (
    <div className={isPortrait ? "mx-5" : "py-2 px-4"}>
      {isPortrait ? (
        // Portrait Layout - Two Columns
        <div className="grid grid-cols-2 gap-4 p-4 place-content-start">
          <div className="grid gap-y-4 place-content-start">
            {leftItems.map(renderItem)}
          </div>
          <div className="grid gap-y-4 place-content-start">
            {rightItems.map(renderItem)}
          </div>
        </div>
      ) : (
        // Landscape Layout - Single Column
        <div>
          {visibleProperties.map((property) =>
            renderPropertyLegendItem(property),
          )}
          {visibleLeaseComps?.map((lc, i) => renderLeaseCompLegendItem(lc, i))}
        </div>
      )}
    </div>
  );
};

export default MarketMapLegends;
